import React, { useMemo } from 'react';
import cn from 'classnames';
import dayjs from 'dayjs';

import Icon from 'chameleon/ui-stack/ui/base/icon';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import ManualTimeSelection from 'chameleon-src/components/assign/AssignManageForm/options/TimeOptions/DateField/ManualTimeSelection';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

const DateInput = (props) => {
  const { field, timezone, popoverRef, onResetClick = () => {} } = props;

  const date = useMemo(() => {
    const parsed = field.value
      ? dayjs(field.value).utc(false).tz(timezone)
      : null;
    return {
      yearDayMonthLabel: parsed ? parsed.format('MM/DD/YYYY') : null,
      hoursMinutesLabel: parsed ? parsed.format('hh:mm A') : null,
    };
  }, [field.value]);

  const onManualTimeSet = ({ hours, minutes }) =>
    field.onChange(
      dayjs(field.value)
        .utc(false)
        .tz(timezone)
        .set('hour', hours)
        .set('minute', minutes),
    );

  const onReset = (e) => {
    e.stopPropagation();
    field.onChange(null);
    onResetClick();
  };

  return (
    <PopOver
      ref={popoverRef}
      disabled={field.disabled || props?.disabled}
      className="datepicker_popup_container"
      contentClassName="datepicker_popup flex flex-col items-center border border-gray-500"
      buttonClassName="datepicker_trigger"
      isNarrow
      size="medium"
      withPortal
      hideArrow
      accessibility={{
        'aria-label': 'Date Picker',
        role: 'input',
      }}
      trigger={(buttonProps, isPopoverOpen, buttonRef, buttonClassName) => (
        <div
          {...buttonProps}
          ref={buttonRef}
          className={cn(
            buttonClassName,
            `border rounded-3xl w-full px-4 h-[32px]
              flex items-center justify-start
              text-base text-gray-800`,
            {
              'border-black': isPopoverOpen,
              'border-gray-400': !isPopoverOpen,
              'bg-white': !props?.disabled,
              'bg-gray-300': props?.disabled,
              'cursor-pointer': !props?.disabled,
            },
          )}
        >
          <Icon name="calendar" accessibility={{ ariaHidden: true }} />
          {date.hoursMinutesLabel ? (
            <div className="pt-1">
              <span
                data-test-id={DATA_TEST_IDS.DATE_YEARS_MONTH_LABEL}
                className="pl-2"
              >
                {date.yearDayMonthLabel}
              </span>
              <ManualTimeSelection
                value={date.hoursMinutesLabel}
                setManualTime={onManualTimeSet}
                className="pl-2"
                disabled={props?.disabled}
              />
            </div>
          ) : (
            <span
              className="pl-4 flex-1"
              data-test-id={DATA_TEST_IDS.EMPTY_DATEPICKER}
            >
              &#8211;
            </span>
          )}
          {dayjs(field.value).isValid() && (
            <div className="flex flex-1 justify-end">
              <ButtonWithIcon
                icon="remove_circle"
                data-test-id="clear-date-time"
                isIcon
                isNaked
                aria-label="Close"
                className="-mr-4 rounded-l-3xl text-gray-500"
                onClick={onReset}
                disabled={props?.disabled}
              />
            </div>
          )}
        </div>
      )}
    >
      {props.children}
    </PopOver>
  );
};

export default DateInput;
